import { getChainName, getNativeToken } from "../../../anyspend/index.js";
import app from "../../../global-account/app.js";
import { toast } from "../../../global-account/react/index.js";
import { getThirdwebChain, supportedChains } from "../../../shared/constants/chains/supported.js";
import { client } from "../../../shared/utils/thirdweb.js";
import invariant from "invariant";
import { useCallback, useState } from "react";
import { prepareTransaction, sendTransaction as twSendTransaction } from "thirdweb";
import { isAddress } from "viem";
import { useSwitchChain } from "wagmi";
import { useB3Account } from "../components/B3Provider/useB3Account.js";
import { useAccountWallet } from "./useAccountWallet.js";
const partnerId = String(process.env.PUBLIC_THIRDWEB_PARTNER_ID || process.env.NEXT_PUBLIC_THIRDWEB_PARTNER_ID || process.env.PUBLIC_GLOBAL_ACCOUNTS_PARTNER_ID || process.env.NEXT_PUBLIC_GLOBAL_ACCOUNTS_PARTNER_ID);
invariant(partnerId, "Partner ID is required");
export function useUnifiedChainSwitchAndExecute() {
  const {
    switchChainAsync
  } = useSwitchChain();
  const [isSwitchingOrExecuting, setIsSwitchingOrExecuting] = useState(false);
  const {
    isActiveSmartWallet,
    isActiveEOAWallet,
    connectedEOAWallet
  } = useAccountWallet();
  const aaAccount = useB3Account();
  // Handle EOA wallet chain switch and execute transaction
  const handleEOASwitchChainAndSendTransaction = useCallback(async (targetChainId, params) => {
    if (!connectedEOAWallet) {
      toast.error("Please connect your wallet");
      return;
    }
    // Get target chain configuration once
    const targetChain = supportedChains.find(chain => chain.id === targetChainId);
    if (!targetChain) {
      toast.error(`Chain ${targetChainId} is not supported`);
      return;
    }
    const currentChainId = connectedEOAWallet?.getChain()?.id;
    const onCorrectChain = currentChainId === targetChainId;
    // Helper function to execute the transaction
    const executeTransaction = async () => {
      const signer = connectedEOAWallet?.getAccount();
      if (!signer) {
        throw new Error("No account connected");
      }
      // Coinbase Smart Wallet specific chain switching (different behavior from other wallets)
      const walletChain = connectedEOAWallet.getChain();
      if (walletChain?.id !== targetChainId) {
        connectedEOAWallet?.switchChain(getThirdwebChain(targetChainId));
      }
      invariant(isAddress(params.to), "params.to is not a valid address");
      const result = await signer.sendTransaction({
        chainId: targetChainId,
        to: params.to,
        data: params.data,
        value: params.value
      });
      toast.success(`Transaction sent: ${result.transactionHash.slice(0, 10)}...`);
      return result.transactionHash;
    };
    try {
      setIsSwitchingOrExecuting(true);
      if (onCorrectChain) {
        return await executeTransaction();
      }
      const switchingToastId = toast.info(`Switching to ${getChainName(targetChainId)}…`);
      const blockExplorerUrl = targetChain.blockExplorers?.default.url;
      invariant(blockExplorerUrl, "Block explorer URL is required");
      const nativeCurrency = getNativeToken(targetChainId);
      await switchChainAsync({
        chainId: targetChainId,
        addEthereumChainParameter: {
          chainName: targetChain.name,
          rpcUrls: [targetChain.rpcUrls.default.http[0]],
          blockExplorerUrls: [blockExplorerUrl],
          nativeCurrency: {
            name: nativeCurrency.name,
            symbol: nativeCurrency.symbol,
            decimals: nativeCurrency.decimals
          }
        }
      });
      toast.dismiss(switchingToastId);
      return await executeTransaction();
    } catch (e) {
      if (e?.code === -32603 || e?.message?.includes("f is not a function")) {
        // This is a workaround for a bug in the wallet provider.
        toast.success(`Switched to ${getChainName(targetChainId)}. Executing…`);
        return await handleEOASwitchChainAndSendTransaction(targetChainId, params);
      } else {
        console.error(e);
        toast.error(e?.message ?? "Unexpected error");
        return undefined;
      }
    } finally {
      setIsSwitchingOrExecuting(false);
    }
  }, [connectedEOAWallet, switchChainAsync]);
  // Handle AA wallet transaction (no chain switch needed for AA)
  const handleAASendTransaction = useCallback(async (targetChainId_0, params_0) => {
    if (!aaAccount) {
      toast.error("Smart wallet not connected");
      return;
    }
    try {
      setIsSwitchingOrExecuting(true);
      const chain_0 = getThirdwebChain(targetChainId_0);
      const transaction = prepareTransaction({
        client,
        chain: chain_0,
        to: params_0.to,
        data: params_0.data,
        value: params_0.value
      });
      // Check if we can use global-accounts-intents, if yes, create an intent.
      try {
        await app.service("global-accounts-intents").create({
          partnerId: partnerId,
          chainId: targetChainId_0,
          to: params_0.to,
          data: params_0.data || "0x",
          value: params_0.value.toString()
        });
      } catch (err_0) {
        console.error("Create global-accounts-intents error", err_0);
      }
      toast.info("Sending transaction…");
      const start = performance.now();
      const sendTxResponse = await twSendTransaction({
        account: aaAccount,
        transaction
      });
      const end = performance.now();
      console.log("Time taken to send transaction", end - start);
      toast.success("Transaction sent successfully");
      return sendTxResponse.transactionHash;
    } catch (err) {
      console.error("Send transaction error", err);
      toast.error(err?.message ?? "Transaction failed");
      return undefined;
    } finally {
      setIsSwitchingOrExecuting(false);
    }
  }, [aaAccount]);
  // Unified switch chain and execute function
  const switchChainAndExecute = useCallback(async (targetChainId_1, params_1) => {
    // Check which wallet type is active
    if (isActiveSmartWallet) {
      return handleAASendTransaction(targetChainId_1, params_1);
    }
    return handleEOASwitchChainAndSendTransaction(targetChainId_1, params_1);
  }, [isActiveSmartWallet, handleAASendTransaction, handleEOASwitchChainAndSendTransaction]);
  return {
    switchChainAndExecute,
    switchChainAndExecuteWithEOA: handleEOASwitchChainAndSendTransaction,
    isSwitchingOrExecuting,
    isActiveSmartWallet,
    isActiveEOAWallet
  };
}